import type { FC } from 'react';
import { useTranslation, Trans } from 'react-i18next';
// @dynamic-i18n-keys
// studio.discountModal.form.appliesToCategoryInput.option.*
//   studio.discountModal.form.appliesToProductInput.option.*
import { useFormContext } from 'react-hook-form';
import c from 'classnames';
import { useFlag } from '@wix/da-react-context/pkg/flags/hooks';
import { DiscountFormField } from '../../../types';
import Heading from '@wix/da-a11y/pkg/components/headings/Heading';
import { Aside } from '@wix/da-a11y/pkg/components/headings/tagWrappers';
import { getAppliesToBabelKeyNew, getAppliesToBabelKeyOld } from './helpers';
import { formatDate } from '@wix/da-i18next/pkg/time/functions';
import s from './DiscountPreviewCard.scss';

interface Props {
  className?: string;
}
const DiscountPreviewCard: FC<Props> = ({ className }) => {
  const { t } = useTranslation();
  const { watch } = useFormContext();
  const studioDiscountChanges = useFlag('studio_discounts_changes');
  const name = watch(DiscountFormField.Name);
  const appliesToCategory = watch(DiscountFormField.AppliesToCategory);
  const appliesToProduct = watch(DiscountFormField.AppliesToProduct);
  const audience = watch(DiscountFormField.Audience);
  const discountValue = watch(DiscountFormField.DiscountValue);
  const startTimestamp = watch(DiscountFormField.StartTimestamp);
  const endTimestamp = watch(DiscountFormField.EndTimestamp);
  const appliesToString = t(
    studioDiscountChanges
      ? getAppliesToBabelKeyNew(appliesToProduct)
      : getAppliesToBabelKeyOld(appliesToCategory, appliesToProduct)
  );

  return (
    <Aside className={c(s['root'], 'ds-card', className)}>
      <Heading className={s['heading']}>
        {t('studio.discountPreview.heading')}
      </Heading>
      <div className={s['description']}>
        {name && (
          <div>
            <strong>{name}</strong>
          </div>
        )}
        {discountValue &&
          (studioDiscountChanges
            ? appliesToProduct === 'any_tier' ||
              (appliesToProduct && discountValue !== '100')
            : appliesToCategory !== 'shop_products' ||
              (appliesToProduct && discountValue !== '100')) && ( // Free only for subscriptions
            <div>
              <strong>
                {t('studio.discountPreview.percentDiscount', {
                  discount: discountValue,
                })}
              </strong>{' '}
              {(studioDiscountChanges
                ? appliesToProduct === 'any_tier'
                : appliesToCategory === 'subscriptions') && (
                <span>{t('studio.discountPreview.onFirstMonthOnly')}</span>
              )}{' '}
              on <strong className={s['appliesTo']}>{appliesToString}</strong>
            </div>
          )}
        {startTimestamp && (
          <div>
            Starts{' '}
            <strong>
              {formatDate(startTimestamp, {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
              })}
            </strong>{' '}
            at{' '}
            <strong>
              {formatDate(startTimestamp, {
                hour: 'numeric',
                hour12: true,
              })}
            </strong>
          </div>
        )}
        {endTimestamp && (
          <div>
            Ends{' '}
            <strong>
              {formatDate(endTimestamp, {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
              })}
            </strong>{' '}
            at{' '}
            <strong>
              {formatDate(endTimestamp, {
                hour: 'numeric',
                hour12: true,
              })}
            </strong>
          </div>
        )}
        {audience && (
          <div>
            {/* Trans is used due to <strong> tags in the translation */}
            <Trans
              i18nKey={`studio.discountPreview.audience.openTo.${audience}`}
            />
          </div>
        )}
      </div>
    </Aside>
  );
};
DiscountPreviewCard.displayName = 'DiscountPreviewCard';

export default DiscountPreviewCard;
